import { z } from "zod";
import {
	BLOCK_INTERACTION_HOLD_MS,
	estimateTextMs,
} from "../../timeline/reading-rate";
import {
	asRecord,
	blockProvenanceHintSchema,
	compact,
	emptyBlockSettingsSchema,
	joinParts,
	optionalStr,
	pickProvenance,
	str,
	TOPIC_BLOCK_INSTRUCTION_TEMPLATES,
} from "../content-helpers";
import {
	type BlockGateInteraction,
	type BlockInteractionSpec,
	defineBlockKind,
	type RecordedBlockInteractionKind,
	STRUCTURED_BLOCK_PATCH_OPS,
} from "../define-block-kind";
import { ATOM_BLOCK_DOC_NODE } from "../doc-node";

/** Longest reflection a learner can save (characters). */
export const REFLECTION_MAX_LENGTH = 4_000;

function bool(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

function finiteInt(value: unknown): number | null {
	return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function requiredGate(
	content: Record<string, unknown>,
	kind: RecordedBlockInteractionKind,
): readonly BlockGateInteraction[] {
	return content.required === true ? [{ kind }] : [];
}

/** An interaction that carries no payload (reveal opened, comparison viewed). */
function markInteraction(
	kind: RecordedBlockInteractionKind,
): BlockInteractionSpec {
	return {
		kind,
		parsePayload: (_content, raw) =>
			raw == null ||
			(typeof raw === "object" &&
				!Array.isArray(raw) &&
				Object.keys(raw).length === 0)
				? {}
				: null,
		revisable: false,
	};
}

// ─── POLL ────────────────────────────────────────────────────────────────────

export const POLL_MODES = ["single", "multi", "scale"] as const;
export type PollMode = (typeof POLL_MODES)[number];

export const POLL_SCALE_MAX_STEPS = 11;

export const pollOptionSchema = z
	.object({
		/** Stable within the poll; votes and results reference it. */
		id: z.string().min(1),
		label: z.string(),
	})
	.strict();

export const pollScaleSchema = z
	.object({
		min: z.number().int(),
		max: z.number().int(),
		minLabel: z.string().optional(),
		maxLabel: z.string().optional(),
	})
	.strict()
	.refine(
		(scale) =>
			scale.max > scale.min &&
			scale.max - scale.min < POLL_SCALE_MAX_STEPS,
		"Scale needs 2–11 steps",
	);

export const pollBlockContentSchema = z
	.object({
		question: z.string(),
		mode: z.enum(POLL_MODES),
		/** `single` / `multi` choices; empty for `scale`. */
		options: z.array(pollOptionSchema),
		/** `scale` range; absent otherwise. */
		scale: pollScaleSchema.optional(),
		/** The card gate waits for a vote. */
		required: z.boolean(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type PollBlockContent = z.infer<typeof pollBlockContentSchema>;
export type PollOption = z.infer<typeof pollOptionSchema>;

/** `poll.vote` payload: chosen option ids (`single` / `multi`) or a scale value. */
export const pollVotePayloadSchema = z.union([
	z.object({ optionIds: z.array(z.string().min(1)).min(1) }).strict(),
	z.object({ value: z.number().int() }).strict(),
]);
export type PollVotePayload = z.infer<typeof pollVotePayloadSchema>;

const DEFAULT_POLL_SCALE = { min: 1, max: 5 } as const;

function normalizePollOptions(raw: unknown): PollOption[] {
	const seen = new Set<string>();
	const options: PollOption[] = [];
	for (const [index, entry] of (Array.isArray(raw) ? raw : []).entries()) {
		const row =
			typeof entry === "string" ? { label: entry } : asRecord(entry);
		const label = str(row.label);
		if (label.length === 0) {
			continue;
		}
		let id = str(row.id) || `option-${index + 1}`;
		while (seen.has(id)) {
			id = `${id}-${index + 1}`;
		}
		seen.add(id);
		options.push({ id, label });
	}
	return options;
}

function normalizePollScale(raw: unknown): PollBlockContent["scale"] {
	const row = asRecord(raw);
	const min = finiteInt(row.min) ?? DEFAULT_POLL_SCALE.min;
	const max = finiteInt(row.max) ?? DEFAULT_POLL_SCALE.max;
	const valid = max > min && max - min < POLL_SCALE_MAX_STEPS;
	return compact({
		min: valid ? min : DEFAULT_POLL_SCALE.min,
		max: valid ? max : DEFAULT_POLL_SCALE.max,
		minLabel: optionalStr(row.minLabel),
		maxLabel: optionalStr(row.maxLabel),
	});
}

function normalizePollContent(raw: Record<string, unknown>): PollBlockContent {
	const mode: PollMode = (POLL_MODES as readonly string[]).includes(
		String(raw.mode),
	)
		? (raw.mode as PollMode)
		: "single";
	return compact({
		question: str(raw.question),
		mode,
		options: mode === "scale" ? [] : normalizePollOptions(raw.options),
		scale: mode === "scale" ? normalizePollScale(raw.scale) : undefined,
		required: bool(raw.required, false),
		...pickProvenance(raw),
	});
}

function parsePollVote(
	content: Record<string, unknown>,
	raw: unknown,
): PollVotePayload | null {
	const poll = normalizePollContent(content);
	const parsed = pollVotePayloadSchema.safeParse(raw);
	if (!parsed.success) {
		return null;
	}
	const vote = parsed.data;
	if ("value" in vote) {
		const scale = poll.scale;
		return poll.mode === "scale" &&
			scale &&
			vote.value >= scale.min &&
			vote.value <= scale.max
			? vote
			: null;
	}
	if (poll.mode === "scale") {
		return null;
	}
	const ids = [...new Set(vote.optionIds)];
	const known = new Set(poll.options.map((option) => option.id));
	if (
		ids.some((id) => !known.has(id)) ||
		(poll.mode === "single" && ids.length !== 1)
	) {
		return null;
	}
	return { optionIds: ids };
}

function pollReadableText(content: Record<string, unknown>): string {
	const poll = normalizePollContent(content);
	return joinParts([
		poll.question,
		poll.options.map((option) => option.label).join("\n"),
	]);
}

export const pollBlockKind = defineBlockKind({
	type: "POLL",
	contentSchema: pollBlockContentSchema,
	defaultContent: {
		question: "",
		mode: "single",
		options: [],
		required: false,
	},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: normalizePollContent,
	deriveNarrationText: (content) =>
		joinParts([
			TOPIC_BLOCK_INSTRUCTION_TEMPLATES.POLL,
			pollReadableText(content),
		]),
	estimateDwellMs: (content) =>
		estimateTextMs(pollReadableText(content)) +
		BLOCK_INTERACTION_HOLD_MS.POLL,
	// A poll nobody can vote on (no options yet) never holds the card.
	gate: (content) =>
		content.mode === "scale" ||
		(Array.isArray(content.options) && content.options.length > 0)
			? requiredGate(content, "poll.vote")
			: [],
	interactions: [
		{ kind: "poll.vote", parsePayload: parsePollVote, revisable: false },
	],
	exportPolicy: { pdf: "question_and_options" },
});

// ─── REVEAL ──────────────────────────────────────────────────────────────────

/** `peel` = React Bits sticker peel; `cards` = React Bits modal cards (one per item). */
export const REVEAL_STYLES = ["peel", "cards"] as const;
export type RevealStyle = (typeof REVEAL_STYLES)[number];

export const revealItemSchema = z
	.object({
		/** What the learner sees before revealing. */
		front: z.string(),
		/** What the reveal shows. */
		back: z.string(),
	})
	.strict();

export const revealBlockContentSchema = z
	.object({
		prompt: z.string(),
		style: z.enum(REVEAL_STYLES),
		items: z.array(revealItemSchema).min(1),
		/** The card gate waits for the reveal (default on). */
		required: z.boolean(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type RevealBlockContent = z.infer<typeof revealBlockContentSchema>;
export type RevealItem = z.infer<typeof revealItemSchema>;

function normalizeRevealContent(
	raw: Record<string, unknown>,
): RevealBlockContent {
	const items = (Array.isArray(raw.items) ? raw.items : [])
		.map((item) => {
			const row = asRecord(item);
			return { front: str(row.front), back: str(row.back) };
		})
		.filter((item) => item.front.length > 0 || item.back.length > 0);
	return compact({
		prompt: str(raw.prompt),
		style: raw.style === "cards" ? "cards" : "peel",
		items: items.length > 0 ? items : [{ front: "", back: "" }],
		required: bool(raw.required, true),
		...pickProvenance(raw),
	});
}

function revealReadableText(content: Record<string, unknown>): string {
	const reveal = normalizeRevealContent(content);
	return joinParts([
		reveal.prompt,
		...reveal.items.flatMap((item) => [item.front, item.back]),
	]);
}

export const revealBlockKind = defineBlockKind({
	type: "REVEAL",
	contentSchema: revealBlockContentSchema,
	defaultContent: {
		prompt: "",
		style: "peel",
		items: [{ front: "", back: "" }],
		required: true,
	},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: normalizeRevealContent,
	// The hidden side is spoken only after the reveal, so narration carries the prompt and fronts.
	deriveNarrationText: (content) => {
		const reveal = normalizeRevealContent(content);
		return joinParts([
			reveal.prompt,
			...reveal.items.map((item) => item.front),
			TOPIC_BLOCK_INSTRUCTION_TEMPLATES.REVEAL,
		]);
	},
	estimateDwellMs: (content) =>
		estimateTextMs(revealReadableText(content)) +
		BLOCK_INTERACTION_HOLD_MS.REVEAL,
	gate: (content) => requiredGate(content, "reveal.open"),
	interactions: [markInteraction("reveal.open")],
	exportPolicy: { pdf: "revealed" },
});

// ─── COMPARISON ──────────────────────────────────────────────────────────────

/** `before_after` = React Bits comparison slider; `do_dont` = paired guidance. */
export const COMPARISON_VARIANTS = ["before_after", "do_dont"] as const;
export type ComparisonVariant = (typeof COMPARISON_VARIANTS)[number];

export const comparisonSideSchema = z
	.object({
		label: z.string(),
		body: z.string(),
		/** `ContentAsset` id of the side's image; the host resolves it to a URL. */
		assetId: z.string().min(1).optional(),
		altText: z.string().optional(),
	})
	.strict();

export const comparisonBlockContentSchema = z
	.object({
		variant: z.enum(COMPARISON_VARIANTS),
		title: z.string().optional(),
		left: comparisonSideSchema,
		right: comparisonSideSchema,
		caption: z.string().optional(),
		/** The card gate waits until the learner has explored both sides. */
		required: z.boolean(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type ComparisonBlockContent = z.infer<
	typeof comparisonBlockContentSchema
>;
export type ComparisonSide = z.infer<typeof comparisonSideSchema>;

const COMPARISON_DEFAULT_LABELS: Record<
	ComparisonVariant,
	{ left: string; right: string }
> = {
	before_after: { left: "Before", right: "After" },
	do_dont: { left: "Don't", right: "Do" },
};

function normalizeComparisonSide(
	raw: unknown,
	fallbackLabel: string,
): ComparisonSide {
	const row = asRecord(raw);
	return compact({
		label: str(row.label) || fallbackLabel,
		body: str(row.body),
		assetId: optionalStr(row.assetId),
		altText: optionalStr(row.altText),
	});
}

function normalizeComparisonContent(
	raw: Record<string, unknown>,
): ComparisonBlockContent {
	const variant: ComparisonVariant =
		raw.variant === "do_dont" ? "do_dont" : "before_after";
	const labels = COMPARISON_DEFAULT_LABELS[variant];
	return compact({
		variant,
		title: optionalStr(raw.title),
		left: normalizeComparisonSide(raw.left, labels.left),
		right: normalizeComparisonSide(raw.right, labels.right),
		caption: optionalStr(raw.caption),
		required: bool(raw.required, false),
		...pickProvenance(raw),
	});
}

function comparisonReadableText(content: Record<string, unknown>): string {
	const comparison = normalizeComparisonContent(content);
	return joinParts([
		comparison.title ?? "",
		`${comparison.left.label}: ${comparison.left.body}`,
		`${comparison.right.label}: ${comparison.right.body}`,
		comparison.caption ?? "",
	]);
}

export const comparisonBlockKind = defineBlockKind({
	type: "COMPARISON",
	contentSchema: comparisonBlockContentSchema,
	defaultContent: {
		variant: "before_after",
		left: { label: "Before", body: "" },
		right: { label: "After", body: "" },
		required: false,
	},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: normalizeComparisonContent,
	deriveNarrationText: comparisonReadableText,
	estimateDwellMs: (content) =>
		estimateTextMs(comparisonReadableText(content)) +
		BLOCK_INTERACTION_HOLD_MS.COMPARISON,
	gate: (content) => requiredGate(content, "comparison.view"),
	interactions: [markInteraction("comparison.view")],
	exportPolicy: { pdf: "side_by_side" },
});

// ─── REFLECTION ──────────────────────────────────────────────────────────────

export const reflectionBlockContentSchema = z
	.object({
		prompt: z.string(),
		/** Placeholder hint inside the answer field. */
		placeholder: z.string().optional(),
		/** Characters a submission needs (0 = any non-empty answer). */
		minLength: z.number().int().min(0).max(REFLECTION_MAX_LENGTH),
		/** The card gate waits for a submission. */
		required: z.boolean(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type ReflectionBlockContent = z.infer<
	typeof reflectionBlockContentSchema
>;

/** `reflection.submit` payload: the learner's own words (private; never aggregated). */
export const reflectionSubmitPayloadSchema = z
	.object({ text: z.string() })
	.strict();
export type ReflectionSubmitPayload = z.infer<
	typeof reflectionSubmitPayloadSchema
>;

function normalizeReflectionContent(
	raw: Record<string, unknown>,
): ReflectionBlockContent {
	const minLength = finiteInt(raw.minLength) ?? 0;
	return compact({
		prompt: str(raw.prompt),
		placeholder: optionalStr(raw.placeholder),
		minLength: Math.min(REFLECTION_MAX_LENGTH, Math.max(0, minLength)),
		required: bool(raw.required, false),
		...pickProvenance(raw),
	});
}

function parseReflectionSubmit(
	content: Record<string, unknown>,
	raw: unknown,
): ReflectionSubmitPayload | null {
	const parsed = reflectionSubmitPayloadSchema.safeParse(raw);
	if (!parsed.success) {
		return null;
	}
	const text = parsed.data.text.trim();
	const { minLength } = normalizeReflectionContent(content);
	return text.length > 0 &&
		text.length >= minLength &&
		text.length <= REFLECTION_MAX_LENGTH
		? { text }
		: null;
}

export const reflectionBlockKind = defineBlockKind({
	type: "REFLECTION",
	contentSchema: reflectionBlockContentSchema,
	defaultContent: { prompt: "", minLength: 0, required: false },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: normalizeReflectionContent,
	deriveNarrationText: (content) =>
		joinParts([
			TOPIC_BLOCK_INSTRUCTION_TEMPLATES.REFLECTION,
			str(content.prompt),
		]),
	estimateDwellMs: (content) =>
		estimateTextMs(str(content.prompt)) +
		BLOCK_INTERACTION_HOLD_MS.REFLECTION,
	gate: (content) => requiredGate(content, "reflection.submit"),
	interactions: [
		{
			kind: "reflection.submit",
			parsePayload: parseReflectionSubmit,
			revisable: true,
		},
	],
	exportPolicy: { pdf: "prompt_and_lines" },
});
